import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { GOODSTUFF_PAGE } from "@/data/content";
import { useSound } from "@/lib/SoundContext";
import { TapeLabel, Stamp } from "@/components/Bits";

// red gingham tablecloth — the good stuff deserves a picnic
const GINGHAM = {
  backgroundColor: "#FFF7E6",
  backgroundImage:
    "repeating-linear-gradient(0deg, rgba(214,53,43,0.10) 0 44px, transparent 44px 88px), repeating-linear-gradient(90deg, rgba(214,53,43,0.10) 0 44px, transparent 44px 88px)",
};

// portrait video slot — the project film lives here (loops muted, sound on tap)
function VideoBox({ project, testid }) {
  const [muted, setMuted] = useState(true);
  return (
    <div data-testid={testid}
      className="relative flex-none w-56 sm:w-72 aspect-[9/16] bg-white border-[4px] border-ink rounded-2xl shadow-[0_12px_0_rgba(43,35,32,0.15)] overflow-hidden -rotate-[0.6deg]">
      {project.video ? (
        <>
          <video src={project.video} autoPlay loop muted={muted} playsInline className="w-full h-full object-cover" />
          {project.hasAudio && (
            <button
              data-testid={`${testid}-sound-toggle`}
              onClick={() => setMuted((m) => !m)}
              className="absolute bottom-3 right-3 rounded-full bg-ink text-[#FFF3D6] border-[3px] border-ink px-3 py-1.5 font-hand font-bold text-xs cursor-pointer hover:scale-110 active:scale-95 transition-transform"
            >
              {muted ? "sound off" : "sound on"}
            </button>
          )}
        </>
      ) : project.poster ? (
        /* film cover until the loop file arrives — taps through to the reel */
        <a href={project.link} target="_blank" rel="noreferrer" className="absolute inset-0 block group" data-testid={`${testid}-link`}>
          <img src={project.poster} alt={project.title} className="absolute inset-0 w-full h-full object-cover" />
          <span className="absolute inset-x-3 bottom-3 rounded-lg bg-[#FFF3D6]/95 border-[3px] border-ink px-3 py-2 font-hand font-bold text-sm text-center shadow-[0_4px_0_rgba(43,35,32,0.3)] group-hover:scale-105 transition-transform">
            watch the film ↗
          </span>
        </a>
      ) : (
        <div className="absolute inset-3 border-[3px] border-dashed border-ink/30 rounded-xl flex flex-col items-center justify-center gap-3">
          <div className="w-14 h-14 rounded-full bg-[#E8402F] border-[3px] border-ink flex items-center justify-center shadow-[0_4px_0_rgba(43,35,32,0.3)]">
            <svg viewBox="0 0 24 24" className="w-6 ml-0.5" aria-hidden="true">
              <path d="M7 4 l13 8 -13 8 z" fill="#FFF3D6" stroke="#2B2320" strokeWidth="2" strokeLinejoin="round" />
            </svg>
          </div>
          <span className="font-hand font-bold text-lg text-ink/50 text-center leading-tight px-3">[project film<br />coming soon]</span>
        </div>
      )}
    </div>
  );
}

// one gallery pic slot — taps through to the real post
function GalleryBox({ item, testid }) {
  const inner = (
    <div data-testid={testid}
      className="flex-none w-32 h-40 sm:w-36 sm:h-44 bg-white border-[3px] border-ink rounded-lg shadow-[0_6px_0_rgba(43,35,32,0.15)] overflow-hidden rotate-[0.8deg] hover:rotate-0 hover:scale-105 transition-transform cursor-pointer">
      {item.image ? (
        <img src={item.image} alt="" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <span className="font-hand font-bold text-sm text-ink/40">[add pic]</span>
        </div>
      )}
    </div>
  );
  return item.link ? (
    <a href={item.link} target="_blank" rel="noreferrer">{inner}</a>
  ) : (
    inner
  );
}

function ProjectBlock({ project, index }) {
  const { play } = useSound();
  const scroller = useRef(null);
  return (
    <motion.section
      className="mt-14"
      data-testid={`project-${project.id}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ type: "spring", stiffness: 130, damping: 15 }}
    >
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <TapeLabel className="!text-sm !px-4">{project.title}</TapeLabel>
        {project.tagline && <Stamp>{project.tagline}</Stamp>}
        <span className="font-hand text-xs text-ink/50">project {index + 1} of 3</span>
      </div>

      {/* video + overview */}
      <div className="flex flex-col md:flex-row gap-6 items-start">
        <VideoBox project={project} testid={`video-${project.id}`} />
        <div className="paper flex-1 border-[4px] border-ink rounded-[10px] p-6 sm:p-8 rotate-[0.4deg] shadow-[0_12px_0_rgba(43,35,32,0.15)]"
          data-testid={`text-${project.id}`}>
          <h2 className="font-display text-2xl sm:text-4xl">{project.title}</h2>
          <p className="font-hand text-lg text-ink/80 mt-3 leading-snug">{project.text}</p>
        </div>
      </div>

      {/* gallery strip */}
      <div className="relative mt-6">
        <div
          ref={scroller}
          className="flex gap-4 overflow-x-auto pb-3 pr-16 scroll-smooth"
          style={{ scrollbarWidth: "none" }}
          data-testid={`gallery-${project.id}`}
        >
          {project.gallery.map((g, i) => (
            <GalleryBox key={i} item={g} testid={`gallery-${project.id}-${i}`} />
          ))}
          <button
            data-testid={`gallery-arrow-${project.id}`}
            onClick={() => {
              play("tick");
              scroller.current?.scrollBy({ left: scroller.current.clientWidth * 0.9, behavior: "smooth" });
            }}
            aria-label="more pics"
            className="absolute right-1 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-ink text-[#FFF3D6] border-[3px] border-ink shadow-[0_4px_0_rgba(43,35,32,0.3)] font-bold text-xl cursor-pointer hover:scale-110 active:scale-95 transition-transform"
          >
            →
          </button>
        </div>
      </div>
    </motion.section>
  );
}

export default function GoodStuffPage() {
  const P = GOODSTUFF_PAGE;
  return (
    <div className="min-h-screen pb-24" style={GINGHAM} data-testid="goodstuff-page">
      <div className="relative z-10 max-w-6xl mx-auto px-4 pt-24">
        <Link to="/fridge" data-testid="back-to-fridge" className="font-hand font-bold text-ink/70 hover:text-ink text-lg">
          ← back to the fridge
        </Link>

        <motion.div
          className="paper border-[4px] border-ink rounded-[10px] p-7 sm:p-10 mt-6 rotate-[0.4deg] shadow-[0_14px_0_rgba(43,35,32,0.15)]"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 130, damping: 15 }}
          data-testid="goodstuff-context-card"
        >
          <div className="flex flex-wrap items-center gap-3">
            <TapeLabel>{P.kicker}</TapeLabel>
            <Stamp>freshly directed</Stamp>
          </div>
          <h1 className="font-display text-4xl sm:text-6xl mt-3" data-testid="goodstuff-title">{P.title}</h1>
          <p className="font-hand text-lg sm:text-xl text-ink/80 mt-3 leading-snug max-w-3xl" data-testid="goodstuff-context">
            {P.context}
          </p>
        </motion.div>

        {P.projects.map((p, i) => (
          <ProjectBlock key={p.id} project={p} index={i} />
        ))}

        <p className="font-hand text-center mt-14 text-lg text-ink/60">
          three courses of the good stuff — more always cooking.
        </p>
      </div>
    </div>
  );
}
